/** What the app tells the HUD: at construction (`HudOptions`), and whenever a setting changes (`HudViewOptions`). */

import type { BindingsPort } from '../../input/bindings';
import { DEFAULT_DIFFICULTY, type HudAid } from './hudAids';
import { asTextSize, type HudTextSize } from './textSize';

/** Player-facing display options (08 §2.7, §4); all can change mid-mission via `Hud.setOptions`. */
export interface HudViewOptions {
  /** The original's single top-left box instead of the corner panels. */
  hud1987: boolean;
  /** Swap the enemy / alert accent for the colour-blind-safe one (05 §4.3). */
  colourBlind: boolean;
  /** x1, x1.5 or x2 on the HUD's prose (textSize.ts); the instruments keep their size. */
  textSize: HudTextSize;
  /**
   * The operation's difficulty, 0 Milk Run to 3 Suicide (`Operation.difficulty`): it sets the DEFAULT for the
   * two aids below (hudAids.ts). Practice, Classic and the Daily Drop have no difficulty of their own and get
   * Standard. It never overrides a player who has asked for an aid: `'on'` always wins.
   */
  difficulty: number;
  /** An arc around the ranger for every enemy who is noticing him. `'auto'`: whatever the difficulty says. */
  detectionArcs: HudAid | boolean;
  /** Ring the radius of his own loudest recent noise. `'auto'`: whatever the difficulty says. */
  noiseRing: HudAid | boolean;
  /** Edge-of-screen pips for gunfire, shouts, explosions and the Osprey. Never difficulty-gated (hudAids.ts). */
  soundPips: boolean;
}

export const DEFAULT_VIEW_OPTIONS: Readonly<HudViewOptions> = {
  hud1987: false,
  colourBlind: false,
  textSize: 1,
  difficulty: DEFAULT_DIFFICULTY,
  detectionArcs: 'auto',
  noiseRing: 'auto',
  soundPips: true,
};

/** Copy whatever was given onto the live options, field by field, leaving the rest alone. */
export function applyViewOptions(view: HudViewOptions, options: Partial<HudViewOptions>): void {
  if (options.hud1987 !== undefined) view.hud1987 = options.hud1987;
  if (options.colourBlind !== undefined) view.colourBlind = options.colourBlind;
  if (options.textSize !== undefined) view.textSize = asTextSize(options.textSize);
  if (options.difficulty !== undefined) view.difficulty = options.difficulty;
  if (options.detectionArcs !== undefined) view.detectionArcs = options.detectionArcs;
  if (options.noiseRing !== undefined) view.noiseRing = options.noiseRing;
  if (options.soundPips !== undefined) view.soundPips = options.soundPips;
}

export interface HudOptions {
  /** Called when the player picks an action on the debrief or pause overlay. */
  onRestart(): void;
  onResume(): void;
  /** What the pause menu calls `onRestart`: a campaign mission is suspended, never restarted (04 §10). */
  leaveLabel?: string;
  /** The bindings store: the pause menu's controls list and the weapon panel's key hints read it. */
  bindings?: BindingsPort;
  /** Daily Drop "no pause-abuse" (04 §11): the pause overlay hides the playfield completely. */
  hidePlayfieldOnPause?: boolean;
  /** Initial display options; the same as calling `setOptions` straight after construction. */
  view?: Partial<HudViewOptions>;
}
